import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import api from '../services/api';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
    const { user, token } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [socket, setSocket] = useState(null);

    const fetchNotifications = useCallback(async () => {
        if (!user) return;
        try {
            console.log('Fetching notifications for user:', user.id);
            const response = await api.get('/api/notifications');
            console.log('Fetched notifications:', response.data.length);
            setNotifications(response.data);
            setUnreadCount(response.data.filter(n => !n.is_read).length);
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        }
    }, [user]);

    useEffect(() => {
        if (user && token) {
            fetchNotifications();

            const backendUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000';
            console.log('Connecting to socket at:', backendUrl);

            const newSocket = io(backendUrl, {
                auth: { token },
                transports: ['websocket'],
                reconnection: true,
                reconnectionAttempts: 5,
                reconnectionDelay: 1000
            });

            newSocket.on('connect', () => {
                console.log('Socket connected successfully with ID:', newSocket.id);
            });

            newSocket.on('connect_error', (error) => {
                console.error('Socket connection error:', error.message);
            });

            newSocket.on('notification', (notification) => {
                console.log('REAL-TIME notification received:', notification);
                setNotifications(prev => {
                    // Avoid duplicates if fetch and socket overlap
                    if (prev.find(n => n.id === notification.id)) return prev;
                    return [notification, ...prev];
                });
                setUnreadCount(prev => prev + 1);
                
                // Optional: Show browser notification if permitted
                if (Notification.permission === 'granted') {
                    new Notification(notification.title, {
                        body: notification.message
                    });
                }
            });

            // 3.5. Register Web Push Subscription for Android status bar notifications
            const registerPush = async () => {
                if ('serviceWorker' in navigator && 'PushManager' in window) {
                    try {
                        const registration = await navigator.serviceWorker.ready;
                        let subscription = await registration.pushManager.getSubscription();
                        
                        let permission = Notification.permission;
                        if (permission === 'default') {
                            permission = await Notification.requestPermission();
                        }
                        
                        if (permission === 'granted') {
                            const vapidPublicKey = 'BNRWRmMOL7slj5XILJ6rAiC-N3OGEr7RUqiTOOhlipbx2EcZoV50G72TL-dspdnflHcudrL_ffCPKm0Zsoswycw';
                            
                            // Base64 helper
                            const padding = '='.repeat((4 - vapidPublicKey.length % 4) % 4);
                            const base64 = (vapidPublicKey + padding).replace(/\-/g, '+').replace(/_/g, '/');
                            const rawData = window.atob(base64);
                            const outputArray = new Uint8Array(rawData.length);
                            for (let i = 0; i < rawData.length; ++i) {
                                outputArray[i] = rawData.charCodeAt(i);
                            }

                            if (!subscription) {
                                subscription = await registration.pushManager.subscribe({
                                    userVisibleOnly: true,
                                    applicationServerKey: outputArray
                                });
                                console.log('[PWA] Push Subscription generated:', subscription);
                            }

                            await api.post('/api/notifications/subscribe', subscription);
                            console.log('[PWA] Registered subscription with backend.');
                        }
                    } catch (pushErr) {
                        console.warn('[PWA] Push subscription skipped/failed:', pushErr.message);
                    }
                }
            };
            registerPush();

            setSocket(newSocket);

            return () => {
                console.log('Closing socket connection');
                newSocket.close();
            };
        } else {
            setNotifications([]);
            setUnreadCount(0);
            if (socket) socket.close();
            setSocket(null);
        }
    }, [user, token, fetchNotifications]);

    const markAsRead = async (id) => {
        try {
            console.log('Marking notification as read:', id);
            await api.patch(`/api/notifications/${id}/read`);
            setNotifications(prev => 
                prev.map(n => n.id === id ? { ...n, is_read: true } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
        }
    };

    const markAllAsRead = async () => {
        try {
            console.log('Marking all notifications as read for user:', user.id);
            const response = await api.patch('/api/notifications/read-all');
            console.log('Mark all read response:', response.data);
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Failed to mark all as read:', error);
        }
    };

    return (
        <NotificationContext.Provider value={{ 
            notifications, 
            unreadCount, 
            markAsRead, 
            markAllAsRead,
            fetchNotifications 
        }}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};
